// DevFpsBadge — a tiny "FPS: 60" pill in the top-left corner while
// running under Vite's dev server. Reads the live FPS from
// window.__planetFps, which the PerformanceMonitor writes every
// sample (default 250ms). We re-read on a 500ms interval so the
// text is legible but cheap. Outside DEV the component renders
// nothing (import.meta.env.DEV is statically false after build).

import { useEffect, useRef, useState } from 'react';

const POLL_MS = 500;

export function DevFpsBadge() {
  // Hard gate: don't even subscribe in production. import.meta.env.DEV
  // is statically false after the Vite build, so the bundle ends up
  // with an inline early-return and a small constant.
  if (!import.meta.env.DEV) return null;

  const [fps, setFps] = useState<number | null>(null);
  const elapsedRef = useRef(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (typeof window === 'undefined') return;
      const w = window as Window & { __planetFps?: number };
      const next = typeof w.__planetFps === 'number' ? w.__planetFps : null;
      setFps(next);
      elapsedRef.current += POLL_MS;
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  const display = fps == null ? '...' : `${Math.round(fps)}`;
  // Color the badge by perf tier — green ≥ 50, amber 30..50, red < 30.
  const tone =
    fps == null
      ? '#888'
      : fps >= 50
        ? '#7fb89f'
        : fps >= 30
          ? '#b8a572'
          : '#c46969';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 90,
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: 11,
        letterSpacing: '0.04em',
        padding: '4px 8px',
        borderRadius: 6,
        color: tone,
        background: 'rgba(10, 10, 15, 0.7)',
        border: `1px solid ${tone}55`,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        pointerEvents: 'none',
      }}
    >
      FPS: {display}
    </div>
  );
}

export default DevFpsBadge;

// Augment the global Window type with the FPS probe the
// PerformanceMonitor mirrors. Keeps TypeScript happy when the badge
// reads window.__planetFps outside the Canvas.
declare global {
  interface Window {
    /** Last sampled FPS as reported by drei's <PerformanceMonitor>.
     *  Read by DevFpsBadge; written by PlanetScene. */
    __planetFps?: number;
  }
}
